import React, { useState } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import api from 'utils/api'
import { fetchUserData, setFirstTimeSuccModal } from '../../Manage/actions'
import { useDispatch, useSelector } from 'react-redux'
import { API_ROOT } from '../../constants'
import { showUpgradeCardModal, setShowPaymentModal } from 'actions'
import { PLANS, PLANS_ID } from 'constants/plans'
import CustomModal from 'components/CustomModal/CustomModal'
import { createNewSite } from 'components/CreateUpdateSiteModal/actions'
import { createNewSiteRequest, getUpdatedFields } from 'components/CreateUpdateSiteModal/validate'
import { trackUpload } from 'utils/analytics'
import * as Sentry from '@sentry/react'

const ReplaceUpgradeModal = ({ show, message, domain, onClose, title, onSiteAction, executeRecaptcha }) => {
  const dispatch = useDispatch()
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingStep, setProcessingStep] = useState('')
  const [error, setError] = useState(null)

  const savedFormValues = useSelector((state) => state.manage.createSiteModal?.savedFormValues)
  const savedInitValues = useSelector((state) => state.manage.createSiteModal?.savedInitValues)
  const userProfile = useSelector((state) => state.manage.userProfile)
  const siteType = useSelector((state) => state.manage.siteType)

  const handleAction = async (action) => {
    if (action === 'UPGRADE') {
      dispatch(setShowPaymentModal({ showPaymentModal: true, upgradePlanId: PLANS.PRO.id }))
      onClose()
      return
    }

    if (action === 'REPLACE') {
      setIsProcessing(true)
      setError(null)

      try {
        setProcessingStep('Processing...')
        await api.post(`${API_ROOT}/pro/delete`, {
          domain: domain?.subdomain,
          site: domain
        })

        setProcessingStep('Preparing upload...')
        dispatch(fetchUserData(false))

        if (savedFormValues && savedInitValues) {
          setProcessingStep('Uploading new site...')

          const executeRecaptchaWrapper = async () => {
            try {
              return await executeRecaptcha()
            } catch (error) {
              console.error(error)
              Sentry.captureException(error, {
                tags: {
                  section: 'create-update-site-recaptcha-error'
                }
              })
              return null
            }
          }

          // Created a progress tracking function similar to the original
          const uploadProgress = ({ loaded, total }, { size }, isMultipartUpload = false) => {
            const progressPercent = isMultipartUpload
              ? Math.round((100 * loaded) / total)
              : Math.round(((100 * loaded) / total) * 0.5)

            // Update the loading step with progress
            setProcessingStep(`Uploading new site... ${progressPercent}%`)
          }

          const data = await createNewSite(
            createNewSiteRequest({
              domainValues: savedFormValues.domainValues,
              toggleValues: savedFormValues.toggleValues,
              uploadValues: getUpdatedFields(savedFormValues.uploadValues, savedInitValues.uploadValues),
              siteType
            }),
            executeRecaptchaWrapper,
            uploadProgress
          )

          const plan = PLANS_ID[userProfile?.productId]?.label || 'Free'
          trackUpload(plan, data?.link?.size, data?.link?.type, userProfile?.email)

          dispatch(
            setFirstTimeSuccModal({
              showFistTimeSuccModal: true,
              firstTimeSite: data.link
            })
          )
          dispatch(fetchUserData(false))
          setIsProcessing(false)
          onClose()
        } else {
          throw new Error('Missing required form data for site creation')
        }
      } catch (error) {
        console.error('Error during site replacement:', error)
        setError('There was an error replacing your site. Please try again.')
        setIsProcessing(false)
        setProcessingStep('')
      }
    }
  }

  return (
    <CustomModal
      aria-labelledby="contained-modal-title-vcenter"
      show={show}
      onHide={!isProcessing ? onClose : null}
      animation={false}
      centered
      enforceFocus={false}
      backdrop={isProcessing ? 'static' : true}
      keyboard={!isProcessing}
    >
      <Modal.Header className="p-3 pb-2 border-0 justify-start">
        <Modal.Title className="bold color-purple-light text-center">
          {isProcessing ? 'Replacing Site' : title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-24px font-18">
        {isProcessing ? (
          <div className="text-center">
            <Spinner animation="border" role="status" className="mb-3" />
            <div className="text-center">
              <div className="mb-2 font-16 flex flex-column align-items-center g-2">
                <span className="font-16 color-purple-light">{processingStep}</span>
                <span>
                  <strong>Note:</strong> This process may take a few minutes. Please do not close this window.
                </span>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div dangerouslySetInnerHTML={{ __html: message }} />
            {error && (
              <div className="alert alert-danger mt-3" role="alert">
                {error}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      {!isProcessing && (
        <Modal.Footer className="border-0">
          <Button variant="outline-primary" onClick={() => handleAction('REPLACE')}></Button>
          <Button variant="primary" onClick={() => handleAction('UPGRADE')}>
            Upgrade
          </Button>
        </Modal.Footer>
      )}
    </CustomModal>
  )
}

export default ReplaceUpgradeModal
